import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import type { ResidentTodo } from '@/types/residents/todos';

/**
 * RTK Query slice for resident personal todos (spec 2026-08-11). Mirrors
 * todoListsApi's conventions: `createApi` + `baseQueryWithReauth`, url style
 * `api/resident-todos...` (no leading slash), `{ success, data }` envelope
 * unwrapped in `transformResponse`, per-id tags + a LIST sentinel — never the
 * bare 'ResidentTodo' string.
 *
 * Server rows carry numeric ids and `notes: string | null`; the client type
 * uses string ids and optional notes, so `toClientTodo` maps at this boundary
 * and no widget component changes shape.
 */

const LIST_TAG_ID = 'LIST' as const;

/** Backend success envelope for endpoints that return a payload. */
interface DataEnvelope<T> {
  success: boolean;
  data: T;
}

/** Server row shape for api/resident-todos. */
interface ResidentTodoRow {
  id: number;
  accountId: number;
  title: string;
  dueDate: string;
  completed: boolean;
  notes: string | null;
}

export interface CreateResidentTodoInput {
  title: string;
  dueDate: string;
  notes?: string;
}

export type UpdateResidentTodoInput = { id: string } & Partial<{
  title: string;
  dueDate: string;
  notes: string | null;
  completed: boolean;
}>;

const toClientTodo = (row: ResidentTodoRow): ResidentTodo => ({
  id: String(row.id),
  title: row.title,
  dueDate: row.dueDate,
  completed: row.completed,
  notes: row.notes ?? undefined,
});

export const residentTodosApi = createApi({
  reducerPath: 'residentTodosApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['ResidentTodo'],
  endpoints: builder => ({
    // GET api/resident-todos — all of the caller's todos, date-ascending.
    getResidentTodos: builder.query<ResidentTodo[], void>({
      query: () => 'api/resident-todos',
      transformResponse: (response: DataEnvelope<ResidentTodoRow[]>) =>
        response.data.map(toClientTodo),
      providesTags: result =>
        result
          ? [
              ...result.map(todo => ({ type: 'ResidentTodo' as const, id: todo.id })),
              { type: 'ResidentTodo' as const, id: LIST_TAG_ID },
            ]
          : [{ type: 'ResidentTodo' as const, id: LIST_TAG_ID }],
    }),

    // POST api/resident-todos
    addResidentTodo: builder.mutation<ResidentTodo, CreateResidentTodoInput>({
      query: body => ({ url: 'api/resident-todos', method: 'POST', body }),
      transformResponse: (response: DataEnvelope<ResidentTodoRow>) =>
        toClientTodo(response.data),
      invalidatesTags: [{ type: 'ResidentTodo', id: LIST_TAG_ID }],
    }),

    // PATCH api/resident-todos/:id — serves the edit form AND the checkbox
    // toggle. Optimistically patches the list cache so the checkbox is
    // instant; a failed request visibly rolls back (spec §7/§10).
    updateResidentTodo: builder.mutation<ResidentTodo, UpdateResidentTodoInput>({
      query: ({ id, ...body }) => ({
        url: `api/resident-todos/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: DataEnvelope<ResidentTodoRow>) =>
        toClientTodo(response.data),
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          residentTodosApi.util.updateQueryData('getResidentTodos', undefined, draft => {
            const todo = draft.find(t => t.id === id);
            if (todo) {
              Object.assign(todo, patch);
              // Client type uses `notes?: string`; a null clear maps to undefined.
              if (patch.notes === null) todo.notes = undefined;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'ResidentTodo', id },
        { type: 'ResidentTodo', id: LIST_TAG_ID },
      ],
    }),

    // DELETE api/resident-todos/:id — bare { success: true } response.
    deleteResidentTodo: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `api/resident-todos/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'ResidentTodo', id: LIST_TAG_ID }],
    }),
  }),
});

export const {
  useGetResidentTodosQuery,
  useAddResidentTodoMutation,
  useUpdateResidentTodoMutation,
  useDeleteResidentTodoMutation,
} = residentTodosApi;
